import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Play, ChevronLeft, Search } from 'lucide-react'
import { api } from '@/lib/api'
import { getTeacherName, formatPrice, formatDate, resolveImg } from '@/lib/helpers'

export default function Videos() {
  const [activeCategory, setActiveCategory] = useState('Бүгд')
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [videos, setVideos] = useState<any[]>([])
  const [featuredVideos, setFeaturedVideos] = useState<any[]>([])
  const [categories, setCategories] = useState<string[]>(['Бүгд'])
  const [pagination, setPagination] = useState<any>({ pages: 1 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.videos.featured().then(setFeaturedVideos).catch(console.error)
    api.categories.get().then(res => setCategories(['Бүгд', ...res.videoCategories])).catch(console.error)
  }, [])

  useEffect(() => {
    setLoading(true)
    api.videos.list({ page: currentPage, limit: 12, category: activeCategory, search: searchQuery })
      .then(res => {
        setVideos(res.data)
        setPagination(res.pagination)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [currentPage, activeCategory, searchQuery])

  return (
    <div>
      <section className="max-w-[1024px] mx-auto px-4 pt-8 pb-4">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6">
          <ChevronLeft className="size-4" />
          Нүүр хуудас
        </Link>
        <h1 className="font-display font-black text-3xl text-gray-900 mb-2">Видео контентууд</h1>
        <p className="text-sm text-gray-500">Таны мэдлэгт зориулсан видео хичээл болон ярилцлагууд.</p>
      </section>

      <section className="max-w-[1024px] mx-auto px-4 py-8">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Онцлох видео контентууд</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {featuredVideos.map(video => (
            <Link key={video._id} to={`/videos/${video._id}`} className="group">
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition">
                <div className="h-28 bg-gradient-to-br from-gray-700 to-gray-900 relative flex items-center justify-center overflow-hidden">
                  {resolveImg(video.thumbnail || video.thumbnailUrl) ? (
                    <img src={resolveImg(video.thumbnail || video.thumbnailUrl)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  ) : null}
                  <Play className="size-8 text-white/50 group-hover:text-white/80 transition relative z-10" />
                  <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded z-10">{video.duration}</span>
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-xs text-gray-900 line-clamp-2">{video.title}</h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-[1024px] mx-auto px-4 pb-6">
        <div className="flex items-center gap-3 flex-wrap">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setCurrentPage(1) }}
              className={`rounded-full px-5 py-2 text-sm font-medium transition ${
                activeCategory === cat ? 'bg-accent-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Видео хайх..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1) }}
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
            />
          </div>
        </div>
      </section>

      <section className="max-w-[1024px] mx-auto px-4 pb-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 animate-pulse">
                <div className="h-36 bg-gray-200" />
                <div className="p-4 flex flex-col gap-2">
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 rounded w-1/3 mt-1" />
                  <div className="flex items-center justify-between mt-2">
                    <div className="h-4 bg-gray-200 rounded w-16" />
                    <div className="h-3 bg-gray-200 rounded w-20" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {videos.map(video => (
              <Link key={video._id} to={`/videos/${video._id}`} className="group">
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition">
                  <div className="h-36 bg-gradient-to-br from-gray-700 to-gray-900 relative flex items-center justify-center overflow-hidden">
                    {resolveImg(video.thumbnail || video.thumbnailUrl) ? (
                      <img src={resolveImg(video.thumbnail || video.thumbnailUrl)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    ) : null}
                    <Play className="size-10 text-white/40 group-hover:text-white/70 transition relative z-10" />
                    <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded z-10">{video.duration}</span>
                    {video.isMemberOnly && (
                      <span className="absolute top-2 left-2 bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5 rounded z-10">
                        Зөвхөн гишүүдэд
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-sm text-gray-900 line-clamp-2">{video.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">{getTeacherName(video)}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm font-semibold text-gray-900">{formatPrice(video.price)}</span>
                      <span className="text-xs text-gray-400">{formatDate(video.publishedAt)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 rounded-full text-sm font-medium transition ${
                  currentPage === page ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
